"use client";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import { DialectsEnum, LangEnum } from "@/constants/constants";
import { AllContentItem } from "@/types/content.types";
import { MappedContinueWatching } from "@/types/home.types";
import { Culture, Dialect } from "@/types/setting.types";
import { MappedMyListResponse } from "@/types/user.types";
import React, { createContext, useContext, useEffect, useState } from "react";

type GlobalContextType = {
  continueWatchingWidgetData: MappedContinueWatching | null;
  setContinueWatchingWidgetData: (
    newContinueWatchingData: MappedContinueWatching | null,
  ) => void;
  // search
  allContent?: AllContentItem[];
  fetchAllContent: () => Promise<void>;
  searchDisplayedSuggestion: string[];
  setSearchDisplayedSuggestion: (arr: string[]) => void;

  // dialect
  currentDialect: Dialect;
  availableDialects: Dialect[];
  setCurrentDialect: (dialect: Dialect) => void;

  // cultures
  availableCultures: Culture[];
};

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export function GlobalProvider({
  children,
  dialect,
  availableDialects,
  availableCultures,
}: {
  children: React.ReactNode;
  dialect: Dialect;
  availableDialects: Dialect[];
  availableCultures: Culture[];
}) {
  const [continueWatchingWidgetData, _setContinueWatchingWidgetData] =
    useState<MappedContinueWatching | null>(null);
  const [allContent, setAllContent] = useState<AllContentItem[]>();
  const [currentDialect, setCurrentDialect] = useState<Dialect>(dialect);

  const [searchDisplayedSuggestion, setSearchDisplayedSuggestion] = useState<
    string[]
  >([]);

  const setContinueWatchingWidgetData = (
    newContinueWatchingWidgetData: MappedContinueWatching | null,
  ) => {
    if (newContinueWatchingWidgetData === null) {
      _setContinueWatchingWidgetData(null);
    } else {
      _setContinueWatchingWidgetData({
        ...newContinueWatchingWidgetData,
        updatedAt: Date.now(),
      });
    }
  };

  const fetchAllContent = async () => {
    if (allContent && allContent.length > 0) {
      return;
    }

    const response = await apiSdkInstance.content.getAllContent({
      dialect: currentDialect.dialectAbbreviation,
      lang: LangEnum.EN,
      type: "allWithMicroDrama",
    });

    setAllContent(response);
  };

  return (
    <GlobalContext.Provider
      value={{
        currentDialect,
        setCurrentDialect,
        availableDialects,
        continueWatchingWidgetData,
        setContinueWatchingWidgetData,
        allContent,
        fetchAllContent,
        searchDisplayedSuggestion,
        setSearchDisplayedSuggestion,
        availableCultures,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }
  return context;
}
